// Frame: Asset Distribution (Beneficiary Assignment)
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { assetAggregationService, type UnifiedAsset } from '../../services/assetAggregationService.ts';
import { isFullyAssigned } from '../../utils/distributionUtils';
import Modal from '../ui/Modal';
import ManualContactModal from '../ui/ManualContactModal';
import { SubHeadline } from '../ui/Typography.tsx';
// Import custom SVG icons
import FinancialIcon from '../../assets/icons/financial-icon.svg';
import RealEstateIcon from '../../assets/icons/real-estate-icon.svg';
import PersonalIcon from '../../assets/icons/personal-icon.svg';
import PetsIcon from '../../assets/icons/pets-icon.svg';
import NoAssetsIcon from '../../assets/icons/no-assets-icon.svg';

// Category Definitions with Custom Icons
const CATEGORIES = [
  { key: 'Financial', label: 'Financial', icon: FinancialIcon, description: 'Bank accounts, investments' },
  { key: 'Real Estate', label: 'Real Estate', icon: RealEstateIcon, description: 'Properties and land' },
  { key: 'Personal Items', label: 'Personal', icon: PersonalIcon, description: 'Jewelry, art, vehicles' },
  { key: 'Pets', label: 'Pets', icon: PetsIcon, description: 'Pets and dependents' },
];

// Category-specific callout messages
const CATEGORY_CALLOUTS: Record<string, { title: string; message: string }> = {
  'Financial': {
    title: 'Financial Institution Records',
    message: 'Important: Beneficiary designations are governed by the financial institution. Please ensure this information matches what is on file, as the institution’s records take precedence.'
  },
  'Real Estate': {
    title: 'Property Ownership',
    message: 'Important: Property distribution may be governed by deeds, titles, or trusts. This information is intended for planning and reference.'
  },
  'Personal Items': {
    title: 'Personal Property Distribution',
    message: 'Important: Personal items are typically distributed according to your will or executor instructions.'
  },
  'Pets': {
    title: 'Pet Care Instructions',
    message: 'Important: Pet care instructions help guide who should care for your pets and how they should be supported.'
  }
};

interface BeneficiaryAllocation {
  beneficiaryId: string;
  percentage: number | string;
}

interface DistributionRecord {
  assetId: string;
  primaryBeneficiaries: BeneficiaryAllocation[];
  contingentBeneficiaries: BeneficiaryAllocation[];
  instructions?: string;
  notes?: string;
}

// Legacy interface for migration
interface LegacyDistributionRecord {
  assetId: string;
  primaryBeneficiaryId: string;
  primaryPercentage: number;
  contingentBeneficiaryId?: string;
  instructions?: string;
  notes?: string;
}

interface AssetCardProps {
  asset: UnifiedAsset;
  status: string;
  beneficiary: string | null;
  onClick: (asset: UnifiedAsset) => void;
  showCategoryBadge?: boolean;
}

const AssetCard: React.FC<AssetCardProps> = memo(({ asset, status, beneficiary, onClick, showCategoryBadge }) => (
  <div
    onClick={() => onClick(asset)}
    className={`relative border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${status === 'assigned'
      ? 'border-green-200 bg-green-50/30'
      : 'border-neutral-200 bg-white hover:border-cyan-300'
      }`}
  >
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-stone-800">{asset.name}</span>
          {showCategoryBadge && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded border border-stone-200">
              {asset.category}
            </span>
          )}
          {asset.description && (
            <span className="text-xs text-stone-400">| {asset.description}</span>
          )}
        </div>
        <div className="text-sm text-stone-600">
          {asset.institution || asset.displayType}
        </div>
        {asset.value !== undefined && asset.value > 0 && (
          <div className="text-xs font-mono text-stone-500 mt-1">
            ${asset.value.toLocaleString()}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className={`px-3 py-1 text-xs rounded-full font-medium ${status === 'assigned'
          ? 'bg-green-100 text-green-800'
          : 'bg-stone-100 text-stone-600'
          }`}>
          {status === 'assigned' ? '✓ Assigned' : 'Unassigned'}
        </span>
      </div>
    </div>

    {beneficiary && (
      <div className="mt-3 pt-3 border-t border-dashed border-stone-200 text-sm">
        <span className="text-stone-500 mr-2">{asset.recipientLabel || 'Beneficiary'}:</span>
        <span className="font-medium text-stone-800">{beneficiary}</span>
      </div>
    )}
  </div>
));

// Validation helper
const calculateTotal = (allocations: BeneficiaryAllocation[]): number => {
  return allocations.reduce((sum, a) => sum + (Number(a.percentage) || 0), 0);
};

const Frame246: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  // State
  const [assets, setAssets] = useState<UnifiedAsset[]>([]);
  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [distributions, setDistributions] = useState<Record<string, DistributionRecord>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('Financial');
  const [viewMode, setViewMode] = useState<'category' | 'all'>('category');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<UnifiedAsset | null>(null);
  const [showManualContactModal, setShowManualContactModal] = useState(false);
  const [contactAddTarget, setContactAddTarget] = useState<'primary' | 'contingent'>('primary');

  // Form State - Multiple Beneficiaries
  const [primaryBeneficiaries, setPrimaryBeneficiaries] = useState<BeneficiaryAllocation[]>([
    { beneficiaryId: '', percentage: 0 }
  ]);
  const [contingentBeneficiaries, setContingentBeneficiaries] = useState<BeneficiaryAllocation[]>([]);
  const [instructions, setInstructions] = useState('');
  const [notes, setNotes] = useState('');

  // Validation State
  const [validationError, setValidationError] = useState<string | null>(null);

  // Derive recipient label from the currently-editing asset so all modal copy is consistent.
  // Falls back to 'Beneficiary'/'Beneficiaries' for assets that predate the recipientLabel field.
  const recipientLabelSingular = editingAsset?.recipientLabel || 'Beneficiary';
  const recipientLabelPlural =
    recipientLabelSingular === 'Beneficiary' ? 'Beneficiaries' : `${recipientLabelSingular}s`;

  // Migrate legacy record to new format
  const migrateLegacyRecord = (legacy: LegacyDistributionRecord): DistributionRecord => ({
    assetId: legacy.assetId,
    primaryBeneficiaries: legacy.primaryBeneficiaryId 
      ? [{ beneficiaryId: legacy.primaryBeneficiaryId, percentage: legacy.primaryPercentage || 0 }]
      : [],
    contingentBeneficiaries: legacy.contingentBeneficiaryId
      ? [{ beneficiaryId: legacy.contingentBeneficiaryId, percentage: 0 }]
      : [],
    instructions: legacy.instructions,
    notes: legacy.notes
  });

  // Load Data
  useEffect(() => {
    const loadData = async () => {
      if (session) {
        const unified = assetAggregationService.getAllAssets(session);
        setAssets(unified);
        console.log('[Frame246] Loaded unified assets:', unified);

        // Load Existing Distributions
        const savedDistributions = session.taskProgress?.assetDistribution?.answers?.distributions || {};
        
        // Handle migration from legacy format
        const migratedDistributions: Record<string, DistributionRecord> = {};
        
        if (Array.isArray(savedDistributions)) {
          savedDistributions.forEach((d: LegacyDistributionRecord | DistributionRecord) => {
            if (d.assetId) {
              // Check if it's legacy format (has primaryBeneficiaryId instead of primaryBeneficiaries)
              if ('primaryBeneficiaryId' in d) {
                migratedDistributions[d.assetId] = migrateLegacyRecord(d as LegacyDistributionRecord);
              } else {
                migratedDistributions[d.assetId] = d as DistributionRecord;
              }
            }
          });
        } else {
          Object.entries(savedDistributions).forEach(([key, d]: [string, unknown]) => {
            const record = d as LegacyDistributionRecord | DistributionRecord;
            if ('primaryBeneficiaryId' in record) {
              migratedDistributions[key] = migrateLegacyRecord(record as LegacyDistributionRecord);
            } else {
              migratedDistributions[key] = record as DistributionRecord;
            }
          });
        }


        setDistributions(migratedDistributions);
      }

      // Load Contacts
      try {
        const allContacts = await unifiedContactService.getAllContacts();
        // Only include active contacts
        setContacts(allContacts.filter(c => c.active !== false));
      } catch (error) {
        console.error('Failed to load contacts', error);
      }
    };
    loadData();
  }, [session]);

  // Derived State
  const filteredAssets = useMemo(() => {
    if (viewMode === 'all') return assets;
    return assets.filter(a => a.category === selectedCategory);
  }, [assets, selectedCategory, viewMode]);

  const getStatus = useCallback((asset: UnifiedAsset) => {
    return isFullyAssigned(distributions[asset.id]) ? 'assigned' : 'unassigned';
  }, [distributions]);

  interface ExistingBeneficiary {
    contactId: string;
    name: string;
    percentage?: number | string;
  }

  const activeBeneficiaryName = useCallback((asset: UnifiedAsset) => {
    const local = distributions[asset.id];
    if (local?.primaryBeneficiaries?.length > 0) {
      // Only show beneficiaries whose contact is active
      return local.primaryBeneficiaries
        .map(pb => {
          const contact = contacts.find(c => c.id === pb.beneficiaryId && c.active !== false);
          return contact ? `${contact.firstName} ${contact.lastName} (${pb.percentage}%)` : null;
        })
        .filter(Boolean)
        .join(', ');
    }
    if (asset.existingBeneficiaries && asset.existingBeneficiaries.length > 0) {
      // Only show existing beneficiaries whose contact is active
      return (asset.existingBeneficiaries as ExistingBeneficiary[])
        .filter((b) => contacts.some(c => c.id === b.contactId && c.active !== false))
        .map((b) => b.name)
        .join(', ');
    }
    return null;
  }, [distributions, contacts]);

  const handleAssetClick = useCallback((asset: UnifiedAsset) => {
    setEditingAsset(asset);
    setValidationError(null);

    // Pre-populate form with existing data
    const existing = distributions[asset.id];
    if (existing) {
      setPrimaryBeneficiaries(
        existing.primaryBeneficiaries.length > 0 
          ? existing.primaryBeneficiaries 
          : [{ beneficiaryId: '', percentage: 0 }]
      );
      setContingentBeneficiaries(existing.contingentBeneficiaries || []);
      setInstructions(existing.instructions || '');
      setNotes(existing.notes || '');
    } else if (asset.existingBeneficiaries && asset.existingBeneficiaries.length > 0) {
      // Pre-populate from upstream beneficiaries
      const mapped = asset.existingBeneficiaries
        .filter((b) => b.contactId)
        .map((b) => ({
          beneficiaryId: b.contactId,
          percentage: b.percentage || 0,
        }));
      
      setPrimaryBeneficiaries(mapped.length > 0 ? mapped : [{ beneficiaryId: '', percentage: 0 }]);
      setContingentBeneficiaries([]);
      setInstructions('');
      setNotes('');
    } else {
      // Reset form for new assignment
      setPrimaryBeneficiaries([{ beneficiaryId: '', percentage: 0 }]);
      setContingentBeneficiaries([]);
      setInstructions('');
      setNotes('');
    }

    setIsModalOpen(true);
  }, [distributions]);

  // Add/Remove beneficiary handlers
  const addPrimaryBeneficiary = () => {
    setPrimaryBeneficiaries([...primaryBeneficiaries, { beneficiaryId: '', percentage: 0 }]);
  };

  const removePrimaryBeneficiary = (index: number) => {
    if (primaryBeneficiaries.length > 1) {
      setPrimaryBeneficiaries(primaryBeneficiaries.filter((_, i) => i !== index));
    }
  };

  const updatePrimaryBeneficiary = (index: number, field: 'beneficiaryId' | 'percentage', value: string | number) => {
    const updated = [...primaryBeneficiaries];
    updated[index] = { ...updated[index], [field]: value };
    setPrimaryBeneficiaries(updated);
  };

  const addContingentBeneficiary = () => {
    setContingentBeneficiaries([...contingentBeneficiaries, { beneficiaryId: '', percentage: 0 }]);
  };

  const removeContingentBeneficiary = (index: number) => {
    setContingentBeneficiaries(contingentBeneficiaries.filter((_, i) => i !== index));
  };

  const updateContingentBeneficiary = (index: number, field: 'beneficiaryId' | 'percentage', value: string | number) => {
    const updated = [...contingentBeneficiaries];
    updated[index] = { ...updated[index], [field]: value };
    setContingentBeneficiaries(updated);
  };

  const handleManualContactSubmit = async (contactData: { firstName: string; lastName: string; email?: string; phone?: string }) => {
    const newContact = await unifiedContactService.createContact({
      firstName: contactData.firstName.trim(),
      lastName: contactData.lastName.trim(),
      email: contactData.email?.trim() || '',
      phone: contactData.phone?.trim(),
      relationship: 'beneficiary',
      source: 'manual',
      notes: 'Added during beneficiary assignment'
    });

    const allContacts = await unifiedContactService.getAllContacts(true);
    setContacts(allContacts.filter(c => c.active !== false));

    if (contactAddTarget === 'contingent') {
      const emptyIndex = contingentBeneficiaries.findIndex(c => !c.beneficiaryId);
      if (emptyIndex >= 0) {
        const updated = [...contingentBeneficiaries];
        updated[emptyIndex] = { ...updated[emptyIndex], beneficiaryId: newContact.id };
        setContingentBeneficiaries(updated);
      } else {
        setContingentBeneficiaries(prev => [...prev, { beneficiaryId: newContact.id, percentage: 0 }]);
      }
    } else {
      const emptyIndex = primaryBeneficiaries.findIndex(p => !p.beneficiaryId);
      if (emptyIndex >= 0) {
        const updated = [...primaryBeneficiaries];
        updated[emptyIndex] = { ...updated[emptyIndex], beneficiaryId: newContact.id };
        setPrimaryBeneficiaries(updated);
      } else {
        setPrimaryBeneficiaries(prev => [...prev, { beneficiaryId: newContact.id, percentage: 0 }]);
      }
    }

    setShowManualContactModal(false);
  };

  // Validation
  const validateAllocations = (): boolean => {
    const hasValidPrimary = primaryBeneficiaries.some(p => p.beneficiaryId);
    
    if (!hasValidPrimary) {
      setValidationError('Please select at least one primary beneficiary.');
      return false;
    }
    
    setValidationError(null);
    return true;
  };

  const handleSave = async () => {
    if (!editingAsset || !validateAllocations()) return;

    const record: DistributionRecord = {
      assetId: editingAsset.id,
      primaryBeneficiaries: primaryBeneficiaries.filter(p => p.beneficiaryId),
      contingentBeneficiaries: contingentBeneficiaries.filter(c => c.beneficiaryId),
      instructions: instructions || undefined,
      notes: notes || undefined
    };

    const newDistributions = {
      ...distributions,
      [editingAsset.id]: record
    };

    setDistributions(newDistributions);
    await updateTaskProgress('assetDistribution', { distributions: newDistributions });

    setIsModalOpen(false);
    setEditingAsset(null);
  };

  const handleNext = () => navigate('/frame-247');
  const handleBack = () => navigate('/frame-245');

  // Get callout for current asset category
  const getCategoryCallout = () => {
    if (!editingAsset) return null;
    return CATEGORY_CALLOUTS[editingAsset.category] || null;
  };

  // Get used beneficiary IDs for a section to filter dropdown options
  const getUsedPrimaryIds = () => primaryBeneficiaries.map(p => p.beneficiaryId).filter(Boolean);
  const getUsedContingentIds = () => contingentBeneficiaries.map(c => c.beneficiaryId).filter(Boolean);

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader
          title="Assign Beneficiaries"
        />
        <SubHeadline>
          Designate who should receive each asset and how it should be distributed. Click on a category to assign beneficiaries for your assets.
        </SubHeadline>

        <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-220px)] min-h-[500px]">
          {/* Sidebar */}
          <div className={`w-full md:w-64 flex-shrink-0 bg-white rounded-lg border border-neutral-200 overflow-hidden flex flex-col transition-all duration-300 ${viewMode === 'all' ? 'md:w-0 md:border-0 md:opacity-0 hidden' : ''
            }`}>
            <div className="p-4 border-b border-neutral-100 bg-neutral-50">
              <h3 className="font-semibold text-stone-700">Categories</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {CATEGORIES.map(cat => {
                const count = assets.filter(a => a.category === cat.key).length;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={`w-full flex items-center justify-between p-4 text-left border-l-4 transition-colors ${selectedCategory === cat.key
                      ? 'bg-cyan-50 border-cyan-600 text-cyan-900'
                      : 'border-transparent hover:bg-neutral-50 text-stone-600'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <img src={cat.icon} alt={cat.label} className="w-5 h-5" />
                      <span className="font-medium">{cat.label}</span>
                    </div>
                    <span className="text-xs font-semibold bg-neutral-100 px-2 py-0.5 rounded-full text-stone-500">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 bg-white rounded-lg border border-neutral-200 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-neutral-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <h3 className="font-semibold text-stone-800 text-lg">
                {viewMode === 'all' ? 'All Assets' : `${CATEGORIES.find(c => c.key === selectedCategory)?.label} Assets`}
              </h3>
              <div className="flex items-center gap-3">
                <div className="text-sm text-stone-500">
                  {filteredAssets.length} items
                </div>
                <button
                  onClick={() => setViewMode(prev => prev === 'category' ? 'all' : 'category')}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${viewMode === 'all'
                    ? 'bg-stone-800 text-white border-stone-800'
                    : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                    }`}
                >
                  {viewMode === 'all' ? 'Show Categories' : 'View All'}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 scroll-smooth">
              {filteredAssets.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <img src={NoAssetsIcon} alt="No assets" className="w-16 h-16 mx-auto mb-3 opacity-60" />
                  <p>No assets found.</p>
                </div>
              ) : (
                <>
                  {/* View Mode: ALL (Grouped by Category) */}
                  {viewMode === 'all' && (
                    <div className="space-y-8">
                      {CATEGORIES.map(cat => {
                        const catAssets = filteredAssets.filter(a => a.category === cat.key);
                        if (catAssets.length === 0) return null;

                        return (
                          <div key={cat.key}>
                            <h4 className="flex items-center gap-2 text-stone-700 font-bold text-lg mb-3 pb-2 border-b border-stone-100 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                              <img src={cat.icon} alt={cat.label} className="w-5 h-5" /> {cat.label}
                              <span className="text-xs font-normal text-stone-400 bg-stone-50 px-2 py-0.5 rounded-full">
                                {catAssets.length}
                              </span>
                            </h4>
                            <div className="space-y-3">
                              {catAssets.map(asset => (
                                <AssetCard
                                  key={asset.uniqueId}
                                  asset={asset}
                                  status={getStatus(asset)}
                                  beneficiary={activeBeneficiaryName(asset)}
                                  onClick={handleAssetClick}
                                  showCategoryBadge={false}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* View Mode: CATEGORY (Flat List) */}
                  {viewMode === 'category' && (
                    <div className="space-y-3">
                      {filteredAssets.map(asset => (
                        <AssetCard
                          key={asset.uniqueId}
                          asset={asset}
                          status={getStatus(asset)}
                          beneficiary={activeBeneficiaryName(asset)}
                          onClick={handleAssetClick}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          cancelText="Close"
          okText="Save Assignment"
          onOk={handleSave}
          okDisabled={!primaryBeneficiaries.some(p => p.beneficiaryId)}
          title={editingAsset ? `Assign ${recipientLabelPlural}: ${editingAsset.name}` : `Assign ${recipientLabelPlural}`}
        >
          <div className="space-y-6">
            {/* Category-specific Callout */}
            {getCategoryCallout() && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-orange-800 text-sm">{getCategoryCallout()!.title}</h4>
                    <p className="text-orange-700 text-sm mt-1">{getCategoryCallout()!.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Primary Beneficiaries Section */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Primary {editingAsset?.recipientLabel || 'Beneficiaries'}
              </label>
              <p className="text-xs text-stone-500 mb-3">
                {recipientLabelSingular === 'Beneficiary'
                  ? 'Primary beneficiaries receive the asset first. You may assign multiple people and split percentages. Percentages must total 100%.'
                  : `Primary ${recipientLabelPlural.toLowerCase()} receive this record first. You may assign multiple people and split percentages. Percentages must total 100%.`}
              </p>
              
              <div className="space-y-3">
                {primaryBeneficiaries.map((pb, index) => {
                  // Only show row if beneficiary is active or not selected
                  const isActive = !pb.beneficiaryId || contacts.some(c => c.id === pb.beneficiaryId && c.active !== false);
                  if (!isActive) return null;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <select
                        value={pb.beneficiaryId}
                        onChange={(e) => {
                          if (e.target.value === '__add_new_contact__') {
                            setContactAddTarget('primary');
                            setShowManualContactModal(true);
                            return;
                          }
                          updatePrimaryBeneficiary(index, 'beneficiaryId', e.target.value);
                        }}
                        className="flex-1 border-neutral-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                      >
                        <option value="">Select a person...</option>
                        {contacts
                          .filter(c => c.active !== false && (c.id === pb.beneficiaryId || !getUsedPrimaryIds().includes(c.id)))
                          .map(c => (
                            <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                          ))}
                        <option value="__add_new_contact__">+ Add new contact...</option>
                      </select>
                      <div className="w-24 relative">
                        <input
                          type="number"
                          min="0"
                          value={pb.percentage}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : Number(e.target.value);
                            if (typeof val === 'number' && val < 0) {
                              updatePrimaryBeneficiary(index, 'percentage', 0);
                            } else {
                              updatePrimaryBeneficiary(index, 'percentage', val);
                            }
                          }}
                          className="w-full border-neutral-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500 pl-3 pr-6"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
  
                        {/* Validation Message */}
                        {pb.percentage !== '' && Number(pb.percentage) <= 0 && (
                          <p className="absolute -bottom-5 left-1 text-[10px] text-cyan-600 font-medium whitespace-nowrap">
                            Minimum value is 0%
                          </p>
                        )}
                      </div>

                      {primaryBeneficiaries.length > 1 && (
                        <button
                          onClick={() => removePrimaryBeneficiary(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <button
                onClick={addPrimaryBeneficiary}
                className="mt-3 flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                <Plus className="w-4 h-4" /> Add Primary {recipientLabelSingular}
              </button>
              
              {/* Percentage total indicator */}
              <div className="mt-2 text-xs">
                <div className={`font-medium ${
                  calculateTotal(
                    primaryBeneficiaries.filter(
                      p => p.beneficiaryId && contacts.some(c => c.id === p.beneficiaryId && c.active !== false)
                    )
                  ) === 100
                    ? 'text-green-600'
                    : 'text-stone-600'
                }`}>
                  Total: {
                    calculateTotal(
                      primaryBeneficiaries.filter(
                        p => p.beneficiaryId && contacts.some(c => c.id === p.beneficiaryId && c.active !== false)
                      )
                    )
                  }%
                </div>
              </div>
            </div>

            {/* Contingent Beneficiaries Section */}
            <div className="pt-4 border-t border-neutral-100">
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Contingent {editingAsset?.recipientLabel || 'Beneficiaries'} (Optional)
              </label>
              <p className="text-xs text-stone-500 mb-3">
                {recipientLabelSingular === 'Beneficiary'
                  ? 'Contingent beneficiaries receive the asset only if all primary beneficiaries are unable to.'
                  : `Contingent ${recipientLabelPlural.toLowerCase()} receive this record only if all primary ${recipientLabelPlural.toLowerCase()} are unable to.`}
              </p>
              
              {contingentBeneficiaries.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {contingentBeneficiaries.map((cb, index) => {
                      // Only show row if beneficiary is active or not selected
                      const isActive = !cb.beneficiaryId || contacts.some(c => c.id === cb.beneficiaryId && c.active !== false);
                      if (!isActive) return null;
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <select
                            value={cb.beneficiaryId}
                            onChange={(e) => {
                              if (e.target.value === '__add_new_contact__') {
                                setContactAddTarget('contingent');
                                setShowManualContactModal(true);
                                return;
                              }
                              updateContingentBeneficiary(index, 'beneficiaryId', e.target.value);
                            }}
                            className="flex-1 border-neutral-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                          >
                            <option value="">Select a person...</option>
                            {contacts
                              .filter(c => c.active !== false && (c.id === cb.beneficiaryId || !getUsedContingentIds().includes(c.id)))
                              .map(c => (
                                <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                              ))}
                            <option value="__add_new_contact__">+ Add new contact...</option>
                          </select>
                        <div className="w-24 relative">
                        <input
                          type="number"
                          min="0"
                          value={cb.percentage}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : Number(e.target.value);
                            if (typeof val === 'number' && val < 0) {
                              updateContingentBeneficiary(index, 'percentage', 0);
                            } else {
                              updateContingentBeneficiary(index, 'percentage', val);
                            }
                          }}
                          className="w-full border-neutral-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500 pl-3 pr-6"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
  
                        {/* Validation Message */}
                        {cb.percentage !== '' && Number(cb.percentage) <= 0 && (
                          <p className="absolute -bottom-5 left-1 text-[10px] text-cyan-600 font-medium whitespace-nowrap">
                            Minimum value is 0%
                          </p>
                        )}
                      </div>
                      
                          <button
                            onClick={() => removeContingentBeneficiary(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {/* Percentage total indicator for contingent */}
                  <div className={`mt-2 text-xs font-medium ${
                    calculateTotal(
                      contingentBeneficiaries.filter(
                        c => c.beneficiaryId && contacts.some(ct => ct.id === c.beneficiaryId && ct.active !== false)
                      )
                    ) === 100
                      ? 'text-green-600'
                      : 'text-stone-600'
                  }`}>
                    Total: {
                      calculateTotal(
                        contingentBeneficiaries.filter(
                          c => c.beneficiaryId && contacts.some(ct => ct.id === c.beneficiaryId && ct.active !== false)
                        )
                      )
                    }%
                  </div>
                </>
              ) : null}
              
              <button
                onClick={addContingentBeneficiary}
                className="mt-3 flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                <Plus className="w-4 h-4" /> Add Contingent {recipientLabelSingular}
              </button>
            </div>

            {/* Special Instructions */}
            <div>
              <label htmlFor="instructions" className="block text-sm font-medium text-stone-700 mb-1">Special Instructions</label>
              <textarea
                id="instructions"
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={3}
                className="w-full border-neutral-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                placeholder="E.g., Only to be distributed after age 25..."
              />
            </div>

            {/* Validation Error */}
            {validationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {validationError}
              </div>
            )}

          </div>
        </Modal>

        <ManualContactModal
          open={showManualContactModal}
          onClose={() => setShowManualContactModal(false)}
          onSubmit={handleManualContactSubmit}
          initialData={{ relationship: 'beneficiary' }}
        />
        </ResponsiveContent>

        <ResponsiveFooter
          title="Assign and Approve Asset Distribution"
          subtitle="Assign beneficiaries by asset category"
          progressPercent={50}
          onNext={handleNext}
          onBack={handleBack}
          nextText="Review Plan"
          backText="Back"
        />
    </ResponsiveContainer>
  );
};

export default Frame246;
